/**
 * Survey-level settings (gap map S2, the SOTG editor's survey page): what the
 * respondent sees and how the survey behaves, the bookkeeping staff fill in,
 * the panelist incentive, and translations.
 *
 * Pure. Publish carries all of it with the draft; mfour-agents writes the SOTG
 * columns named beside each field (builder.md, "Survey settings"). `admin` and
 * `incentive` are staff fields: publish sends them only for staff.
 */
import { z } from 'zod';

import { publicEnv } from '@/lib/config/public-env';

import type { BuilderDraftContent } from './draft';

export const INVITE_CHANNELS = ['any', 'mobile', 'desktop', 'mobile_web'] as const;
export type InviteChannel = (typeof INVITE_CHANNELS)[number];

export const BuilderSettingsSchema = z
    .object({
        /** Shown before the first question / after the last (SOTG Introduction, Closing Text). */
        intro_text: z.string().max(4000).optional(),
        closing_text: z.string().max(4000).optional(),
        /** The title over the closing text (SOTG `thankYouTitle`). */
        thank_you_title: z.string().max(20).optional(),
        /** What a respondent who is screened out sees (SOTG `terminateTitle`, `terminateText`). */
        terminate_title: z.string().max(20).optional(),
        terminate_text: z.string().max(250).optional(),
        /** The name respondents see in the app (SOTG Respondent Display Name). */
        display_name: z.string().max(80).optional(),
        /** SOTG showProgressBar; absent shows it. */
        hide_progress_bar: z.literal(true).optional(),
        /** SOTG revisit: respondents may go back to earlier questions. */
        allow_back: z.literal(true).optional(),
        /** SOTG timeLimit: minutes without an answer before the survey closes for them. */
        inactivity_minutes: z.number().int().min(5).max(1440).optional(),
        /** SOTG timeEstimate: the length respondents are told; absent, Builder's estimate. */
        stated_minutes: z.number().int().min(1).max(120).optional(),
        /** SOTG secure: prevent or limit screenshots. */
        block_screenshots: z.literal(true).optional(),
        /** SOTG mobileInviteRequired / desktopInviteRequired / forceWeb. */
        invite_channel: z.enum(INVITE_CHANNELS).optional(),
    })
    .strict();
export type BuilderSettings = z.infer<typeof BuilderSettingsSchema>;

/** Staff bookkeeping (SOTG clientId, sampleFrame, reportingCategoriesId, sfProjectId, sfFinanceProjectId, csTag, growthTag). */
export const BuilderAdminSchema = z
    .object({
        client: z.string().max(120).optional(),
        sample_frame: z.string().max(120).optional(),
        reporting_category: z.string().max(120).optional(),
        salesforce_project: z.string().max(40).optional(),
        salesforce_finance_project: z.string().max(40).optional(),
        cs_expires_soon: z.literal(true).optional(),
        growth_prequalified: z.literal(true).optional(),
        /**
         * The invite-free web link (W2.4, decision 18): true opens the survey's
         * web link to anyone eligible without an invite (SOTG
         * desktopInviteRequired = 0); false keeps the invite. Absent sends
         * nothing, so a client's survey never carries it.
         */
        invite_free_web_link: z.boolean().optional(),
        /** SOTG forceWeb: the app opens the survey as mobile web. Absent sends nothing. */
        force_web: z.boolean().optional(),
        /**
         * A panel profile survey opened from SOTG (W2.4, decision 17). Its
         * answers feed every survey's targeting: changes publish in place and
         * live, answered questions cannot be removed, and it is never created.
         */
        profile_survey: z.literal(true).optional(),
    })
    .strict();
export type BuilderAdmin = z.infer<typeof BuilderAdminSchema>;

/** What panelists earn, in dollars (SOTG payout, partialCredit). Staff only. */
export const BuilderIncentiveSchema = z
    .object({
        complete_dollars: z.number().min(0).max(500),
        /** Paid to people who screen out, when set. */
        partial_credit_dollars: z.number().min(0).max(500).optional(),
    })
    .strict();
export type BuilderIncentive = z.infer<typeof BuilderIncentiveSchema>;

export const CALIBRATION_JOBS = ['fill', 'test', 'anchor', 'pulse'] as const;
export type CalibrationJob = (typeof CALIBRATION_JOBS)[number];

/**
 * A calibration study for the synthetic population (calibration plan):
 * what its answers are for, decided before it fields and never after.
 * `panelist_hash` holds out a fixed 20% by panelist; `whole_sample` holds the
 * whole study out (a market deep dive, a zip cluster). The pipeline applies
 * the holdout when it loads the answers; Builder records the decision.
 */
export const CalibrationSchema = z
    .object({
        job: z.enum(CALIBRATION_JOBS),
        topic_area: z.string().max(80).optional(),
        holdout: z.enum(['panelist_hash', 'whole_sample']),
    })
    .strict();
export type Calibration = z.infer<typeof CalibrationSchema>;

/**
 * Quality rules applied after fielding (SOTG's scrub): a complete faster than
 * this share of the median length is a speeder; a grid answered with one
 * column on every row is straight-lined. Removed respondents are not paid.
 */
export const QualityRulesSchema = z
    .object({
        speeder_share: z.number().min(0.1).max(0.9).optional(),
        straightline_grids: z.literal(true).optional(),
    })
    .strict();
export type QualityRules = z.infer<typeof QualityRulesSchema>;

/** A language code (es, fr, zh-Hant) to translated text by key (translationKeys). */
export const TranslationsSchema = z.record(
    z.string().regex(/^[a-z]{2,3}(-[A-Za-z]{2,4})?$/),
    z.record(z.string().max(80), z.string().max(4000)),
);
export type Translations = z.infer<typeof TranslationsSchema>;

export interface TranslationKey {
    key: string;
    /** The text as written, to translate. */
    source: string;
}

/**
 * Every piece of text a respondent reads, by a stable key: the screens, each
 * question's lead-in and text, and each answer. Keys use question ids and
 * option codes, which never change, so a translation survives reordering.
 */
export function translationKeys(
    draft: Pick<BuilderDraftContent, 'questions' | 'settings'>,
): TranslationKey[] {
    const out: TranslationKey[] = [];
    const push = (key: string, source: string | undefined) => {
        if (source?.trim()) out.push({ key, source });
    };
    push('intro', draft.settings?.intro_text);
    for (const q of draft.questions) {
        push(`${q.question_id}.pre`, q.pre_text);
        push(`${q.question_id}.text`, q.question_text);
        for (const o of q.options) push(`${q.question_id}.answer.${o.option_code}`, o.option_text);
    }
    push('closing', draft.settings?.closing_text);
    return out;
}

const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** A CSV to hand a translator: key, the text as written, and a column to fill. */
export function translationTemplateCsv(
    draft: Pick<BuilderDraftContent, 'questions' | 'settings' | 'translations'>,
    language: string,
): string {
    const done = draft.translations?.[language] ?? {};
    const rows = translationKeys(draft).map((k) =>
        [k.key, k.source, done[k.key] ?? ''].map(csvCell).join(','),
    );
    return [`key,source,${language}`, ...rows].join('\n') + '\n';
}

/** Splits CSV text into rows of cells, honouring quotes. */
function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (quoted) {
            if (c === '"' && text[i + 1] === '"') {
                cell += '"';
                i++;
            } else if (c === '"') quoted = false;
            else cell += c;
        } else if (c === '"') quoted = true;
        else if (c === ',') {
            row.push(cell);
            cell = '';
        } else if (c === '\n' || c === '\r') {
            if (c === '\r' && text[i + 1] === '\n') i++;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
        } else cell += c;
    }
    if (cell !== '' || row.length) {
        row.push(cell);
        rows.push(row);
    }
    return rows.filter((r) => r.some((x) => x.trim() !== ''));
}

/**
 * A filled-in template back into translations: the language from the third
 * column's header, each non-empty translation by key. Keys the survey does not
 * have are reported, not kept.
 */
export function parseTranslationCsv(
    csv: string,
    draft: Pick<BuilderDraftContent, 'questions' | 'settings'>,
): { language: string; texts: Record<string, string>; unknown: string[] } | { error: string } {
    const rows = parseCsv(csv);
    const header = rows[0]?.map((h) => h.trim());
    if (!header || header[0] !== 'key' || header.length < 3) {
        return {
            error: 'Use the downloaded template: its columns are key, source and the language.',
        };
    }
    const language = header[2];
    if (!/^[a-z]{2,3}(-[A-Za-z]{2,4})?$/.test(language)) {
        return { error: `"${language}" is not a language code such as es or fr.` };
    }
    const known = new Set(translationKeys(draft).map((k) => k.key));
    const texts: Record<string, string> = {};
    const unknown: string[] = [];
    for (const r of rows.slice(1)) {
        const key = r[0]?.trim();
        const value = r[2]?.trim();
        if (!key || !value) continue;
        if (known.has(key)) texts[key] = value.slice(0, 4000);
        else unknown.push(key);
    }
    return { language, texts, unknown };
}

/** For each language, the keys still untranslated. */
export function missingTranslations(
    draft: Pick<BuilderDraftContent, 'questions' | 'settings' | 'translations'>,
): Record<string, string[]> {
    const keys = translationKeys(draft).map((k) => k.key);
    return Object.fromEntries(
        Object.entries(draft.translations ?? {}).map(([lang, texts]) => [
            lang,
            keys.filter((k) => !texts[k]?.trim()),
        ]),
    );
}

/** The staff-only parts of a draft: bookkeeping, the incentive and the follow-ups. */
export const STAFF_FIELDS = ['admin', 'incentive', 'chains', 'calibration'] as const;
type StaffFields = Pick<BuilderDraftContent, (typeof STAFF_FIELDS)[number]>;

/**
 * A draft as a client reads it: the staff fields left out. A client's save
 * puts them back from the stored version (`keepStaffFields`), so staff work is
 * never lost and publish always sends the whole draft.
 */
export function withoutStaffFields<T extends Partial<StaffFields>>(draft: T): T {
    const out = { ...draft };
    for (const k of STAFF_FIELDS) delete out[k];
    return out;
}

/** A client's save: whatever it sent for the staff fields, the stored ones stand. */
export function keepStaffFields<T extends Partial<StaffFields>>(
    next: T,
    stored: Partial<StaffFields> | null,
): T {
    const out = withoutStaffFields(next);
    for (const k of STAFF_FIELDS) {
        const v = stored?.[k];
        if (v !== undefined) (out as Record<string, unknown>)[k] = v;
    }
    return out;
}

/**
 * A survey's invite-free web link (W2.4, decision 18), from the survey
 * system's public site (`NEXT_PUBLIC_SOTG_PUBLIC_URL`). Null when the site is
 * not configured. Never carries who is asking: no personal data in a URL.
 */
export function inviteFreeLink(surveyId: string | number | null | undefined): string | null {
    const base = (publicEnv('NEXT_PUBLIC_SOTG_PUBLIC_URL') ?? '').trim().replace(/\/+$/, '');
    if (!base || surveyId === null || surveyId === undefined || !/^\d+$/.test(String(surveyId)))
        return null;
    return `${base}/survey/start/${surveyId}`;
}
